import { useSelector, useDispatch } from 'react-redux';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, ShoppingCart, FileText, Users, Palette, LogOut, Loader2, Tags, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { resetLoginData } from '../../redux/action';
import { API_BASE_URL } from '../../config/api';
import { OrdersPage } from './orders/OrderPage';
import { canAccessPanelPath, isAdminRole, canAccessEstimates } from '../../utils/roles';
import { EstimatesPage } from './estimates/EstimatesPage';
import logoImage from '../../assets/logo.png';

export const PanelPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { role = "", authToken = "", id = "" } = useSelector((state: any) => state.loginData || {});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isAdmin = isAdminRole(role);
  const showEstimates = canAccessEstimates(role);

  const isActive = (path: string) => location.pathname === path;

  // Staff/Dealer cannot open admin-only URLs by typing them
  useEffect(() => {
    if (!canAccessPanelPath(role, location.pathname)) {
      navigate("/panel/orders", { replace: true });
    }
  }, [role, location.pathname, navigate]);

  // All navigation items with role-based filtering
  const navItems = [
    { path: "/panel/orders", label: "Orders", icon: ShoppingCart },
    { path: "/panel/invoices", label: "Invoices", icon: FileText },
    ...(showEstimates ? [{ path: "/panel/estimates", label: "Quotations", icon: ClipboardList }] : []),
    ...(isAdmin ? [{ path: "/panel/colors", label: "Colors", icon: Palette }] : []),
    ...(isAdmin ? [{ path: "/panel/categories", label: "Product Categories", icon: Tags }] : []),
    ...(isAdmin ? [{ path: "/panel/products", label: "Products", icon: ShoppingCart }] : []),
    ...(isAdmin ? [{ path: "/panel/users", label: "Users", icon: Users }] : []),
  ];

  // Mobile bottom bar - simplified with role-based filtering
  const mobileBottomNavItems = [
    { path: "/panel/orders", label: "Orders", icon: ShoppingCart },
    { path: "/panel/invoices", label: "Invoices", icon: FileText },
    ...(showEstimates ? [{ path: "/panel/estimates", label: "Quotations", icon: ClipboardList }] : []),
    ...(isAdmin ? [{ path: "/panel/categories", label: "Categories", icon: Tags }] : []),
    ...(isAdmin ? [{ path: "/panel/products", label: "Products", icon: ShoppingCart }] : []),
  ];

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}account/${id}/logout`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      
      // Clear Redux state
      dispatch(resetLoginData());
      
      // Redirect to login
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still log out the user even if API fails
      dispatch(resetLoginData());
      navigate('/login');
    } finally {
      setLogoutLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex md:flex-col bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white transition-all duration-300 ${
          sidebarOpen ? "md:w-72" : "md:w-24"
        } shadow-2xl`}
      >
        {/* Logo/Header */}
        <div className="p-6 border-b border-blue-800/50 bg-gradient-to-r from-blue-900/80 to-blue-800/80 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3">
            <div
              className={`transition-all duration-300 flex items-center gap-3 min-w-0 ${
                sidebarOpen ? "opacity-100 scale-100 flex-1" : "opacity-100 justify-center flex-1"
              }`}
            >
              <img
                src={logoImage}
                alt="Thaai"
                className={`object-contain shrink-0 drop-shadow-md ${
                  sidebarOpen ? "h-11 w-11" : "h-9 w-9"
                }`}
              />
              {sidebarOpen ? (
                <div className="min-w-0">
                  <div className="font-bold text-2xl bg-gradient-to-r from-cyan-200 via-blue-200 to-blue-300 bg-clip-text text-transparent">
                    Thaai
                  </div>
                  <div className="text-xs text-blue-300 font-semibold">Admin Panel</div>
                </div>
              ) : null}
            </div>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-blue-700/50 rounded-lg transition-all duration-200 hidden lg:block shrink-0 group active:scale-95"
              title={sidebarOpen ? "Collapse" : "Expand"}
            >
              <Menu size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-4 py-8 space-y-2 overflow-y-auto scrollbar-hide relative z-10">
          <div className={`text-xs font-bold uppercase tracking-widest text-blue-300 px-4 mb-6 transition-opacity ${sidebarOpen ? "opacity-100" : "opacity-0 hidden"}`}>
            <div className="flex items-center gap-2">
              <div className="w-1 h-3 bg-gradient-to-b from-cyan-400 to-blue-500 rounded-full"></div>
              Navigation
            </div>
          </div>
          {navItems.map((item) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                  active
                    ? "bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-600 text-white shadow-xl shadow-blue-500/40 transform scale-105"
                    : "text-blue-200 hover:text-white hover:bg-blue-700/40 hover:shadow-lg hover:shadow-blue-500/20"
                }`}
              >
                {/* Animated background for active */}
                {active && (
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-blue-400/20 group-hover:from-cyan-300/30 group-hover:to-blue-300/30 transition-all animate-pulse" />
                )}
                
                <div className={`relative z-10 transition-all ${active ? "scale-125 drop-shadow-lg" : "group-hover:scale-110"}`}>
                  <IconComponent size={22} className={active ? "drop-shadow-md" : ""} />
                </div>
                {sidebarOpen && (
                  <span className={`font-bold text-sm relative z-10 transition-all ${active ? "translate-x-0 drop-shadow" : "group-hover:translate-x-1"}`}>
                    {item.label}
                  </span>
                )}
                {active && !sidebarOpen && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-cyan-300 to-blue-500 rounded-full shadow-lg shadow-cyan-400/50" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer - Logout Button */}
        <div className="border-t border-indigo-800/50 p-4 bg-slate-900/50 backdrop-blur-sm space-y-3">
          <button
            onClick={handleLogout}
            disabled={logoutLoading}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
              sidebarOpen
                ? "bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                : "justify-center"
            } group active:scale-95 disabled:cursor-not-allowed`}
            title={!sidebarOpen ? "Logout" : ""}
          >
            {logoutLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {sidebarOpen && <span>Logging out...</span>}
              </>
            ) : (
              <>
                <LogOut size={18} />
                {sidebarOpen && <span>Logout</span>}
              </>
            )}
          </button>
          
          <div className={`text-xs text-slate-500 text-center transition-opacity ${sidebarOpen ? "opacity-100" : "opacity-0 hidden"}`}>
            <div className="font-semibold text-slate-400">Thaai</div>
            <div>© 2026</div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden pb-28 md:pb-0">
        {/* Top Header Bar */}
        <header className="md:hidden bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-all text-gray-700 hover:text-gray-900 active:scale-95"
              title="Menu"
            >
              <Menu size={24} />
            </button>
            <button
              onClick={handleLogout}
              disabled={logoutLoading}
              className="p-2 hover:bg-red-50 rounded-lg transition-all text-red-600 hover:text-red-700 disabled:opacity-60 active:scale-95"
              title="Logout"
            >
              {logoutLoading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <LogOut size={20} />
              )}
            </button>
          </div>
        </header>

        {/* Main Content Area — keep Orders mounted so returning to the tab is instant */}
        <main className="flex-1 overflow-auto">
          <div className={`p-4 md:p-8 max-w-7xl mx-auto w-full ${isActive("/panel/orders") ? "block" : "hidden"}`}>
            <OrdersPage />
          </div>
          <div className={`p-4 md:p-8 max-w-7xl mx-auto w-full ${isActive("/panel/estimates") ? "block" : "hidden"}`}>
            {showEstimates ? <EstimatesPage /> : null}
          </div>
          {!isActive("/panel/orders") && !isActive("/panel/estimates") && (
            <div className="p-4 md:p-8 max-w-7xl mx-auto w-full">
              <Outlet />
            </div>
          )}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-4 left-4 right-4 md:hidden bg-gradient-to-r from-blue-900 to-blue-800 text-white border-t border-blue-700 shadow-2xl shadow-blue-600/30 rounded-full">
        <div className="flex justify-around items-center h-20 px-2">
          {mobileBottomNavItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-4 py-3 transition-all duration-300 flex-1 rounded-full group relative ${
                  isActive(item.path)
                    ? "text-white bg-blue-700 shadow-lg shadow-blue-600/50"
                    : "text-blue-200 hover:text-white hover:bg-blue-700/40"
                }`}
              >
                <IconComponent size={24} className={isActive(item.path) ? "drop-shadow-lg" : "group-hover:scale-110 transition-transform"} />
                <span className={`text-xs font-semibold transition-all ${isActive(item.path) ? "drop-shadow" : ""}`}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Mobile Menu Modal/Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 md:hidden z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute left-0 top-0 h-full w-64 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white shadow-2xl animate-in slide-in-from-left">
            {/* Menu Header */}
            <div className="p-6 border-b border-blue-800/50 bg-gradient-to-r from-blue-900/80 to-blue-800/80 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={logoImage}
                  alt="Thaai"
                  className="h-10 w-10 object-contain shrink-0 drop-shadow-md"
                />
                <div className="font-bold text-xl bg-gradient-to-r from-cyan-200 via-blue-200 to-blue-300 bg-clip-text text-transparent">
                  Thaai
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-blue-700/50 rounded-lg transition-all"
              >
                <X size={20} />
              </button>
            </div>

            {/* Menu Items */}
            <nav className="px-4 py-8 space-y-2 max-h-calc(100vh-280px) overflow-y-auto">
              <div className="text-xs font-bold uppercase tracking-widest text-blue-300 px-4 mb-6">
                All Pages
              </div>
              {navItems.map((item) => {
                const IconComponent = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden ${
                      active
                        ? "bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-600 text-white shadow-xl shadow-blue-500/40"
                        : "text-blue-200 hover:text-white hover:bg-blue-700/40"
                    }`}
                  >
                    {active && (
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-blue-400/20 group-hover:from-cyan-300/30 group-hover:to-blue-300/30 transition-all animate-pulse" />
                    )}
                    <div className={`relative z-10 transition-all ${active ? "scale-125 drop-shadow-lg" : "group-hover:scale-110"}`}>
                      <IconComponent size={22} className={active ? "drop-shadow-md" : ""} />
                    </div>
                    <span className={`font-bold text-sm relative z-10 transition-all ${active ? "translate-x-0 drop-shadow" : "group-hover:translate-x-1"}`}>
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </nav>

            {/* Menu Footer - Logout Button */}
            <div className="absolute bottom-0 left-0 right-0 border-t border-indigo-800/50 p-4 bg-slate-900/50 space-y-3">
              <button
                onClick={handleLogout}
                disabled={logoutLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 bg-red-600 hover:bg-red-700 text-white disabled:opacity-60 group active:scale-95 disabled:cursor-not-allowed"
              >
                {logoutLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Logging out...</span>
                  </>
                ) : (
                  <>
                    <LogOut size={18} />
                    <span>Logout</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelPage;
